import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom-v5-compat";
import { InjectedFormProps } from "redux-form";
import { Field } from "redux-form/immutable";

import {
  Alert,
  Box,
  Button,
  Heading,
  Icon,
  IconButtonLink,
  Inline,
  Link,
  Stack,
  Text
} from "@seneca/senekit";

import { hasUserPasswordProvider } from "services/firebase/auth/ssoProvider";

import {
  getUserEmail,
  getUserRecoveryEmail,
  isUserManaged
} from "seneca-common/features/user/state";
import { useSimpleNavigationCallback } from "seneca-common/utils/hooks/routing";

import SchoolsDropdownMenu from "features/common/forms/components/SchoolsDropdownMenu";
import SubjectDropdown from "features/common/forms/components/SubjectDropdown";
import YearGroupDropdown from "features/common/forms/components/YearGroupDropdown";
import { isMobile } from "features/common/utils/functions/mobile";
import { FormDropdown } from "features/forms/components/FormDropdownMenu";
import { FormTextInput } from "features/forms/components/FormTextInput";
import {
  getCollectTeacherRole,
  getCollectUserSchool
} from "features/regions/selectors";
import { LinkedAccountsSource } from "features/settings/features/linked-accounts/analytics";
import { isSchoolStudent } from "features/user";
import {
  UserAccountTypeSettingsForm as ACCOUNT_TYPE,
  TeacherRoleOptions
} from "features/user/consts";
import { MODAL_ROUTES, ROUTES } from "routing/routes";

import useShowAccountTypeFieldByVariant from "../hooks/useShowAccountTypeFieldByVariant";
import useShowEmailFieldByVariant from "../hooks/useShowEmailFieldByVariant";
import { FormProps } from "../types";

type FormValuesType = {
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  school?: { schoolId?: string; name?: string };
  accountType?: string | null;
  email?: string | null;
  region?: string | null;
  yearGroup?: number | null;
  nickname?: string | null;
  teacherRole?: string | null;
  teacherSubjects?: string[] | null;
};

type Props = {
  initialValues: FormValuesType;
  isSchoolTeacher: boolean;
  isSchoolParent: boolean;
  includePhoneNumber: boolean;
  includeYearGroup: boolean;
  showRegionField: boolean;
  regionOptions: Array<{
    value: string;
    label: string;
  }>;
} & FormProps &
  InjectedFormProps;

export default function PersonalInformationForm({
  handleSubmit,
  submitting,
  pristine,
  error,
  isSchoolTeacher,
  isSchoolParent,
  includeYearGroup,
  includePhoneNumber,
  showRegionField,
  regionOptions,
  children: { avatar }
}: Props) {
  const email = useSelector(getUserEmail);
  const isManaged = useSelector(isUserManaged);
  const collectTeacherRole = useSelector(getCollectTeacherRole);
  const hasEmailPasswordProvider = hasUserPasswordProvider();
  const showEmailField = useShowEmailFieldByVariant();
  const showSchoolsField = useSelector(getCollectUserSchool) && !isSchoolParent;
  const showAccountTypeField = useShowAccountTypeFieldByVariant();
  const location = useLocation();

  return (
    <Box paddingTop="large" component="form" onSubmit={handleSubmit}>
      <Stack space="xxlarge">
        <Heading level="3"> Your information</Heading>
        {isManaged && <DetailsManagedAlert />}

        <Stack space="xlarge">
          {showEmailField && (
            <Inline space="xlarge" alignItems="center">
              {avatar}
              <Inline
                space="xlarge"
                alignItems="flex-end"
                justifyContent={["space-between", "flex-start"]}
              >
                <Stack space="medium">
                  <Text weight="bold" size="xsmall" tone="neutral">
                    Email
                  </Text>
                  <Text
                    dataNoTranslate
                    weight="bold"
                    size="medium"
                    tone="neutral"
                  >
                    {email}
                  </Text>
                </Stack>

                {hasEmailPasswordProvider && !isManaged && (
                  <Inline>
                    <Box display={["block", "none"]}>
                      <IconButtonLink
                        accessibilityLabel="Edit"
                        to={MODAL_ROUTES.CHANGE_EMAIL.buildPath({})}
                        state={MODAL_ROUTES.CHANGE_EMAIL.buildState({
                          background: location
                        })}
                        iconName="edit"
                        size="small"
                      />
                    </Box>

                    <Box display={["none", "block"]}>
                      <Link
                        to={MODAL_ROUTES.CHANGE_EMAIL.buildPath({})}
                        state={MODAL_ROUTES.CHANGE_EMAIL.buildState({
                          background: location
                        })}
                      >
                        <Box display="flex" alignItems="center">
                          <Icon
                            iconName="edit"
                            size="small"
                            accessibilityLabel={null}
                          />
                          <Box marginLeft="small">Change email</Box>
                        </Box>
                      </Link>
                    </Box>
                  </Inline>
                )}
              </Inline>
            </Inline>
          )}

          <Box
            display="grid"
            gridTemplateColumns={[
              "100%",
              "100%",
              "minmax(0, 50%) minmax(0, 50%)"
            ]}
            gap="xlarge"
          >
            <Field
              name="firstName"
              component={FormTextInput}
              type="text"
              label="First name"
              disabled={isManaged}
            />
            <Field
              name="lastName"
              component={FormTextInput}
              type="text"
              label="Last name"
              disabled={isManaged}
            />

            {showSchoolsField && (
              <Field
                name="school"
                label="School"
                component={SchoolsDropdownMenu}
                isDisabled={isManaged}
              />
            )}

            {showRegionField && (
              <Field
                name="region"
                label="Region"
                component={FormDropdown}
                options={regionOptions}
                isClearable={false}
                isDisabled={isManaged}
                openMenuOnFocus
              />
            )}

            {isSchoolTeacher && collectTeacherRole && (
              <Field
                name="teacherRole"
                label="Role"
                component={FormDropdown}
                placeholder="Select your role"
                options={TeacherRoleOptions}
                isSearchable={!isMobile()}
                openMenuOnFocus
              />
            )}

            {isSchoolTeacher && (
              <Field
                name="teacherSubjects"
                label="Subjects taught"
                component={SubjectDropdown}
              />
            )}

            {includeYearGroup && (
              <Field
                label="Year group"
                name="yearGroup"
                component={YearGroupDropdown}
                isDisabled={isManaged}
              />
            )}

            {isSchoolTeacher && (
              <Field
                name="nickname"
                component={FormTextInput}
                type="text"
                label="Students call you?"
              />
            )}

            {includePhoneNumber && (
              <Field
                name="phoneNumber"
                label="Mobile number"
                component={FormTextInput}
                type="text"
                disabled={isManaged}
              />
            )}

            {showAccountTypeField && (
              <Field
                name="accountType"
                label="Account type"
                component={FormDropdown}
                options={ACCOUNT_TYPE}
                isSearchable={false}
                isDisabled={isManaged}
                openMenuOnFocus
              />
            )}
          </Box>

          {(isSchoolTeacher || !isManaged) && (
            <Box marginTop="xlarge">
              <Button
                loading={submitting}
                disabled={pristine}
                onClick={() => {}}
                width={["100%", "180px"]}
                fill="filled"
              >
                Save changes
              </Button>
            </Box>
          )}
        </Stack>

        {error && (
          <Alert iconName="exclamationCircle" tone="error">
            {error}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}

function DetailsManagedAlert() {
  const hasRecoveryEmail = !!useSelector(getUserRecoveryEmail);
  const isStudent = useSelector(isSchoolStudent);
  const navigateToLinkedAccounts = useSimpleNavigationCallback(
    ROUTES.DASHBOARD.SETTINGS.LINKED_ACCOUNTS.buildPath({}),
    {
      state: {
        source: LinkedAccountsSource.SETTINGS_ALERT
      }
    }
  );

  if (isStudent && !hasRecoveryEmail) {
    return (
      <Alert
        iconName="exclamationCircle"
        buttonProps={{
          width: "100px",
          text: "Connect",
          onClick: navigateToLinkedAccounts
        }}
      >
        Your details are managed by your school system. Talk to the school IT
        department if you need to edit them. Connect a recovery email to ensure
        you never lose access to your account, even if you move schools.
      </Alert>
    );
  }

  return (
    <Alert iconName="exclamationCircle">
      Your details are managed by your school system. Talk to the school IT
      department if you need to edit them.
    </Alert>
  );
}
